import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const fmtVal = (v) => (v === null || v === undefined ? <span className="text-slate-400 italic">NULL</span> : typeof v === "number" ? v.toFixed(Number.isInteger(v) ? 0 : 2) : `'${String(v)}'`);

function ColumnTable({ title, row, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mb-1">{title}</div>
      <div className="rounded-md border border-slate-200 max-h-56 overflow-y-auto">
        <table className="w-full text-[11px]">
          <tbody>
            {Object.entries(row).map(([k, v]) => (
              <tr key={k} className="border-b border-slate-100 last:border-b-0">
                <td className="px-2 py-0.5 font-mono text-slate-600 w-52">{k}</td>
                <td className="px-2 py-0.5 font-mono text-slate-900">{fmtVal(v)}</td>
              </tr>
            ))}
            <tr><td className="px-2 py-0.5 font-mono text-slate-600">row_modified_by</td><td className="px-2 py-0.5 text-slate-500 italic">bridge SQL login</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function GateList({ gates, testIdPrefix }) {
  return (
    <ul className="space-y-0.5 text-[11px]" data-testid={`${testIdPrefix}-gates`}>
      {gates.map((g) => (
        <li key={g.code} className={`flex items-start gap-1.5 ${g.ok ? "text-slate-600" : "text-rose-700 font-medium"}`} data-testid={`${testIdPrefix}-gate-${g.code}`}>
          {g.ok ? <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" /> : <XCircle className="w-3 h-3 mt-0.5 text-rose-600 shrink-0" />}
          <span>{g.message}</span>
        </li>
      ))}
    </ul>
  );
}

// Every column value for the ar_invoice header and the ar_invoice_gl line,
// every gate, then an explicit confirm. User-initiated only.
export default function FoundationARPreviewDialog({ open, onOpenChange, preview, onConfirm, pushing, result, testIdPrefix = "ar" }) {
  if (!preview) return null;
  const blocked = !preview.ok;
  const failed = result && result.outcome !== "success";
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid={`${testIdPrefix}-preview-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Push invoice {preview.invoice_number} to Foundation AR?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-600">
              <p data-testid={`${testIdPrefix}-preview-text`}>
                Writes one <strong>unposted</strong> AR invoice (header + GL line) into <code>{preview.database}</code>. Accounting posts it in Foundation.
                Corrections after a push are made in Foundation, never by re-pushing.
              </p>
              {failed && (
                <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900 space-y-1" data-testid={`${testIdPrefix}-failure`}>
                  <div><strong>Foundation rejected the write</strong> — {result.plain_error || result.message}</div>
                  {result.exception_text && (
                    <pre className="whitespace-pre-wrap break-all font-mono text-[10px] bg-white/70 border border-rose-200 rounded px-1.5 py-1" data-testid={`${testIdPrefix}-failure-raw`}>{result.exception_text}</pre>
                  )}
                  {result.sql_sent && (
                    <pre className="whitespace-pre-wrap break-all font-mono text-[10px] text-slate-700 bg-white/70 border border-slate-200 rounded px-1.5 py-1" data-testid={`${testIdPrefix}-failure-sql`}>{String(result.sql_sent)}</pre>
                  )}
                </div>
              )}
              {preview.already_in_foundation && (
                <div className="rounded-md border border-sky-300 bg-sky-50 p-2 text-xs text-sky-900" data-testid={`${testIdPrefix}-already-notice`}>
                  Already in Foundation — edit there.
                </div>
              )}
              {blocked && (
                <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900" data-testid={`${testIdPrefix}-blocked-notice`}>
                  <strong>Blocked before write</strong> — {preview.blockers.length} gate{preview.blockers.length === 1 ? "" : "s"} failed. Nothing will be sent until every gate passes.
                </div>
              )}
              {preview.warnings?.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 space-y-0.5" data-testid={`${testIdPrefix}-warnings`}>
                  {preview.warnings.map((w, i) => <div key={i} className="flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{w}</div>)}
                </div>
              )}
              <GateList gates={preview.gates} testIdPrefix={testIdPrefix} />
              <div className="grid md:grid-cols-2 gap-3">
                <ColumnTable title="ar_invoice (header)" row={preview.header} testId={`${testIdPrefix}-header-table`} />
                <ColumnTable title="ar_invoice_gl (line 1)" row={preview.lines[0]} testId={`${testIdPrefix}-line-table`} />
              </div>
              {preview.control_ar && (
                <div className="text-[11px] text-slate-500" data-testid={`${testIdPrefix}-control-ar`}>
                  control_ar: enable_cc_dist={preview.control_ar.enable_cc_dist} · hidden_tax_type={preview.control_ar.hidden_tax_type} · control_date_flag={preview.control_ar.control_date_flag}
                  {preview.control_ar.control_date_flag === "P" && ` · period ${String(preview.control_ar.start_date).slice(0, 10)} → ${String(preview.control_ar.end_date).slice(0, 10)}`}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testIdPrefix}-preview-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={blocked || pushing} data-testid={`${testIdPrefix}-preview-confirm`}
                             className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
            {pushing ? "Writing…" : "Confirm — write to Foundation"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
